import { AnimatedLogo } from '@plcy-io/app-core';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';

import { NAV_GROUPS, ROUTES } from '../routes.ts';

import { CtaButton } from './CtaButton.tsx';
import styles from './MarketingLayout.module.css';
import { WaitlistBand } from './WaitlistBand.tsx';

const STATUS_LINE =
  'plcy.io is in pre-launch. Join the waitlist for early access.';

function Caret(): React.ReactElement {
  return (
    <svg
      className={styles['caret']}
      width='10'
      height='6'
      viewBox='0 0 10 6'
      fill='none'
      aria-hidden='true'
      focusable='false'
    >
      <path
        d='M1 1l4 4 4-4'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function BurgerIcon({ open }: { readonly open: boolean }): React.ReactElement {
  return (
    <svg
      width='18'
      height='14'
      viewBox='0 0 18 14'
      fill='none'
      aria-hidden='true'
      focusable='false'
    >
      {open ? (
        <path
          d='M2 1l14 12M16 1L2 13'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
        />
      ) : (
        <path
          d='M1 1h16M1 7h16M1 13h16'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
        />
      )}
    </svg>
  );
}

function menuLinkClass({ isActive }: { isActive: boolean }): string {
  return [styles['menuLink'], isActive ? styles['menuLinkActive'] : undefined]
    .filter(Boolean)
    .join(' ');
}

/**
 * Site chrome for every marketing page: sticky translucent-blur top nav with
 * Product / Platform / Solutions dropdowns and the header waitlist CTA, the
 * routed page via `<Outlet />`, the closing {@link WaitlistBand}, and the
 * footer (all 16 routes, pre-launch status line, copyright).
 *
 * @remarks
 * Pages render inside this layout via the router — they never render their
 * own nav, closing waitlist band, or footer.
 */
export function MarketingLayout(): React.ReactElement {
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef<HTMLElement | null>(null);
  const location = useLocation();

  // Close all menus whenever the route changes. Adjusting state during render
  // by comparing against the previous route (React's "information from previous
  // renders" pattern) is the sanctioned alternative to a synchronous setState
  // inside an effect, which would trigger an extra cascading render.
  const routeKey = `${location.pathname}${location.hash}`;
  const [lastRouteKey, setLastRouteKey] = useState(routeKey);
  if (routeKey !== lastRouteKey) {
    setLastRouteKey(routeKey);
    setOpenGroup(null);
    setMobileOpen(false);
  }

  // Close the open dropdown on outside pointerdown or Escape.
  useEffect(() => {
    if (openGroup === null) {
      return undefined;
    }

    function onPointerDown(event: PointerEvent): void {
      if (
        navRef.current &&
        event.target instanceof Node &&
        !navRef.current.contains(event.target)
      ) {
        setOpenGroup(null);
      }
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpenGroup(null);
      }
    }

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openGroup]);

  return (
    <div className={styles['shell']}>
      <a className={styles['skipLink']} href='#main-content'>
        Skip to content
      </a>

      <header className={styles['header']}>
        <div className={styles['headerInner']}>
          <Link
            to={ROUTES.home}
            className={styles['brand']}
            aria-label='plcy.io — home'
          >
            <AnimatedLogo
              variant='dark'
              animation='none'
              height={26}
              alt='plcy.io'
            />
          </Link>

          <nav
            ref={navRef}
            className={styles['nav']}
            aria-label='Primary navigation'
          >
            {NAV_GROUPS.map((group) => {
              const isOpen = openGroup === group.label;
              return (
                <div key={group.label} className={styles['navItem']}>
                  <button
                    type='button'
                    className={styles['navButton']}
                    aria-expanded={isOpen}
                    aria-haspopup='true'
                    onClick={() => setOpenGroup(isOpen ? null : group.label)}
                  >
                    {group.label}
                    <Caret />
                  </button>
                  {isOpen && (
                    <ul className={styles['menu']}>
                      {group.links.map((link) => (
                        <li key={link.to}>
                          <NavLink to={link.to} className={menuLinkClass} end>
                            {link.label}
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </nav>

          <div className={styles['headerActions']}>
            <span className={styles['headerCta']}>
              <CtaButton href='#waitlist'>Join the waitlist</CtaButton>
            </span>
            <button
              type='button'
              className={styles['menuToggle']}
              aria-expanded={mobileOpen}
              aria-controls='mobile-nav'
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileOpen((open) => !open)}
            >
              <BurgerIcon open={mobileOpen} />
            </button>
          </div>
        </div>

        {mobileOpen && (
          <nav
            id='mobile-nav'
            className={styles['mobilePanel']}
            aria-label='Mobile navigation'
          >
            {NAV_GROUPS.map((group) => (
              <div key={group.label} className={styles['mobileGroup']}>
                <p className={styles['mobileGroupTitle']}>{group.label}</p>
                <ul className={styles['mobileLinks']}>
                  {group.links.map((link) => (
                    <li key={link.to}>
                      <Link to={link.to} className={styles['mobileLink']}>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        )}
      </header>

      <main id='main-content' className={styles['main']}>
        <Suspense
          fallback={
            <div className={styles['routeFallback']} aria-hidden='true' />
          }
        >
          <Outlet />
        </Suspense>
        <WaitlistBand />
      </main>

      <footer className={styles['footer']}>
        <div className={styles['footerInner']}>
          <div className={styles['footerBrand']}>
            <Link to={ROUTES.home} aria-label='plcy.io — home'>
              <AnimatedLogo
                variant='dark'
                animation='none'
                height={28}
                alt='plcy.io'
              />
            </Link>
            <p className={styles['footerTagline']}>
              AI-powered policy management for MGAs, MGUs, and wholesale
              brokers.
            </p>
            <Link to='/why' className={styles['footerExternal']}>
              why plcy.io
            </Link>
            <a
              href='https://blog.plcy.io'
              className={styles['footerExternal']}
              target='_blank'
              rel='noopener noreferrer'
            >
              blog
            </a>
          </div>

          {NAV_GROUPS.map((group) => (
            <nav key={group.label} aria-label={`Footer — ${group.label}`}>
              <p className={styles['footerColTitle']}>{group.label}</p>
              <ul className={styles['footerLinks']}>
                {group.links.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className={styles['footerLink']}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className={styles['footerLegal']}>
          <p className={styles['statusLine']}>{STATUS_LINE}</p>
          <p className={styles['copyright']}>
            plcy.io™, Iris™, Watchtower™, and Coverage Tower™ are trademarks of
            plcy.io. ACORD is a registered trademark of ACORD Corporation.
            Apache Kafka, Kubernetes, Rust, Pingora, Redis, PostgreSQL, Grafana,
            Loki, Prometheus, OpenTelemetry, NetSuite, Sage Intacct, QuickBooks,
            Xero, SAP, and all other third-party marks are trademarks of their
            respective owners. plcy.io is not affiliated with or endorsed by any
            of these organizations.
          </p>
          <p className={styles['copyright']}>
            © {new Date().getFullYear()} plcy.io. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
